import {useState, useEffect} from 'react';
import SongInfo from '../SongInfo/SongInfo';
import Verse from '../Verse/Verse';
import Chorus from '../Chorus/Chorus';
import Melody from '../Melody/Melody';
import Button from '@mui/material/Button/Button';
import ButtonGroup from '@mui/material/ButtonGroup/ButtonGroup';
import {ISongInfoInput} from '../SongInfo/SongInfo';
import {isAxiosError} from 'axios';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import {useNavigate, useLocation} from 'react-router-dom';
import MessageBox from '../../MessageBox/MessageBox';
import './SongForm.css';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { SubmitHandler, useForm } from 'react-hook-form';

const convertTimeFormatToSeconds = (time: string): number => {
    const [minutes, seconds] = time.split(':').map(Number);
    return minutes * 60 + seconds;
};

const convertStringSecondsToTimeFormat = (secondsString: string): string => {
    const seconds = parseInt(secondsString, 10);
    if (isNaN(seconds) || seconds < 0) {
        return '0:00'; // Domyślny format w przypadku błędu
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

export interface ISongDetailsInput {
    lyrics: string;
    acords: string;
}

export interface ISongInputWrapper {
    type: 'verse' | 'chorus' | 'melody';
    data: ISongDetailsInput;
    id: number;
}

interface props {
    songNumber: number;
    info: ISongInfoInput;
    setInfo: (newData: ISongInfoInput) => void;
    setInput: React.Dispatch<React.SetStateAction<ISongInputWrapper[]>>;
    input: ISongInputWrapper[];
    syncInputData: () => void;
    reset: () => void;
    fetchData: (isMounted: boolean, controller: AbortController) => Promise<void>;
}

function isFirst(list: ISongInputWrapper[], element: ISongInputWrapper) {
    if (list[0] === element) {
        return true;
    }

    const index = list.findIndex((e) => e === element);
    return element.type !== list[index - 1].type;
}

const SongForm = (props: props) => {
    const axiosPrivate = useAxiosPrivate();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [result, setResult] = useState(true);
    const [openParseSongDialog, setOpenParseSongDialog] = useState(false);
    const [songText, setSongText] = useState('');
    const bandId = sessionStorage.getItem('editedBandId');
    const [isLoading, setIsLoading] = useState(false);

    const SUCCESS_MESSAGE = 'Dane zostały poprawnie zapisane!';
    const ERROR_MESSAGE = 'Podczas zapisu danych wystąpił błąd!';
    const ERROR_MESSAGE_FORM = 'Pola wymagane nie mogą być puste!';

    const VERSE_URL = bandId ? `/admin/stanzas/${bandId}` : '/stanzas';
    const CHORUS_URL = bandId ? `/admin/refrains/${bandId}` : '/refrains';
    const MELODY_URL = bandId ? `/admin/melodies/${bandId}` : '/melodies';
    const SONG_URL = bandId ? `/admin/songs/${bandId}` : '/songs';
    const INFO_URL = bandId ? `/admin/songs/${bandId}` : '/songs';
    const MP3_URL = bandId ? `/admin/songsFiles/${bandId}` : '/songsFiles';
    const PARSE_SONG_URL = '/parseSong';

    const verses = props.input.filter((input) => input.type === 'verse');
    const choruses = props.input.filter((input) => input.type === 'chorus');
    const melodies = props.input.filter((input) => input.type === 'melody');

    const [errorMsg, setErrorMsg] = useState('');
    const [existingFileName, setExistingFileName] = useState<string | null>(null);

    const [sectionErrors, setSectionErrors] = useState<{
        verses: boolean[];
        choruses: boolean[];
        melodies: boolean[];
    }>({
        verses: [],
        choruses: [],
        melodies: [],
    });

     const {
            control,
            handleSubmit,
            getValues,
            setValue,
            formState: {errors},
        } = useForm<ISongInfoInput>({
            defaultValues: props.info,
        });


    const GenerateSongStructure = () => (
        <>
            {props.input.map((input) => {
                switch (input.type) {
                    case 'verse':
                        return (
                            <Verse
                                input={input}
                                setInput={props.setInput}
                                syncInputData={props.syncInputData}
                                delete={deleteInput}
                                key={props.input.indexOf(input)}
                                songNumber={props.songNumber}
                                id={input.id}
                                first={isFirst(props.input, input)}
                                fetchData={props.fetchData}
                            />
                        );
                    case 'chorus':
                        return (
                            <Chorus
                                input={input}
                                setInput={props.setInput}
                                syncInputData={props.syncInputData}
                                delete={deleteInput}
                                key={props.input.indexOf(input)}
                                songNumber={props.songNumber}
                                id={input.id}
                                first={isFirst(props.input, input)}
                                fetchData={props.fetchData}
                            />
                        );
                    case 'melody':
                        return (
                            <Melody
                                input={input}
                                setInput={props.setInput}
                                syncInputData={props.syncInputData}
                                delete={deleteInput}
                                key={props.input.indexOf(input)}
                                songNumber={props.songNumber}
                                id={input.id}
                                fetchData={props.fetchData}
                            />
                        );
                }
            })}
        </>
    );

    const deleteInput = async (
        input: ISongInputWrapper,
        id: number,
        songNumber: number,
        type: "verse" | "chorus" | "melody"
    ) => {
        let URL = "";
        switch (type) {
            case "verse":
                URL = VERSE_URL;
                break;
            case "chorus":
                URL = CHORUS_URL;
                break;
            case "melody":
                URL = MELODY_URL;
                break;
        }
    
        try {
            await axiosPrivate.delete(URL, {
                headers: { "Content-Type": "application/json" },
                data: {
                    songNumber: songNumber,
                    id: id,
                },
            });
    
            props.setInput((prev) => {
                const updatedInputs = prev
                    .filter((i) => !(i.id === id && type === i.type)) // Usunięcie wybranego elementu
                    .map((item, index) => ({
                        ...item,
                        id: index + 1, // Ponowne indeksowanie
                    }));
    
                return updatedInputs;
            });
    
            setResult(true);
            const isMounted = true;
            const controller = new AbortController();
            // props.fetchData(isMounted, controller);
        } catch (error) {
            console.log("EROREK: ", error);
            if (isAxiosError(error)) {
                if (error.response && error.response.status === 403) {
                    navigate("/", { state: { from: location }, replace: true });
                }
                setResult(false);
            }
        }
    
        setOpen(true);
    };
    
    const handleParseSong = async () => {
        setIsLoading(true);
        try {
            // Najpierw usuń istniejący utwór
            await props.reset();
            
            const response = await axiosPrivate.post(
                PARSE_SONG_URL,
                JSON.stringify({ songText }),
                {
                    headers: { 'Content-Type': 'application/json' },
                }
            );
            
            // Wrap the response in a root element and add XML declaration
            const wrappedXml = `<?xml version="1.0" encoding="UTF-8"?><song>${response.data}</song>`;
            
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(wrappedXml, "text/xml");
            
            // Check for parsing errors
            const parserError = xmlDoc.querySelector('parsererror');
            if (parserError) {
                console.error('XML parsing error:', parserError.textContent);
                throw new Error('Failed to parse XML');
            }

            props.setInfo({
                ...props.info,
                title: xmlDoc.querySelector('tytul')?.textContent || '',
                lyrics: xmlDoc.querySelector('autor_slow')?.textContent || '',
                author: xmlDoc.querySelector('autor_muzyki')?.textContent || '',
                length: props.info.length,
                lengthInt: props.info.lengthInt,
                id: props.info.id,
                songNumber: props.info.songNumber
            });

            const newStructure: ISongInputWrapper[] = [];
            let currentId = 0;

            const sections = xmlDoc.querySelectorAll('zwrotka, refren, melodia');
            
            sections.forEach(section => {
                let type: 'verse' | 'chorus' | 'melody';
                
                switch (section.tagName.toLowerCase()) {
                    case 'zwrotka':
                        type = 'verse';
                        break;
                    case 'refren':
                        type = 'chorus';
                        break;
                    case 'melodia':
                        type = 'melody';
                        break;
                    default:
                        console.warn(`Nieznany typ sekcji: ${section.tagName}`);
                        return;
                }

                if (type === 'melody') {
                    newStructure.push({
                        type: type,
                        data: {
                            lyrics: section.textContent || '',
                            acords: ''
                        },
                        id: currentId++
                    });
                } else {
                    const lyrics: string[] = [];
                    const chords: string[] = [];
                    
                    section.querySelectorAll('w, chwyty').forEach(element => {
                        if (element.tagName === 'w') {
                            lyrics.push(element.textContent || '');
                        } else if (element.tagName === 'chwyty') {
                            chords.push(element.textContent || '');
                        }
                    });

                    newStructure.push({
                        type: type,
                        data: {
                            lyrics: lyrics.join('\n'),
                            acords: chords.join('\n')
                        },
                        id: currentId++
                    });
                }
            });

            props.setInput(newStructure);
            setOpenParseSongDialog(false);

        } catch (error) {
            console.error('Error parsing song:', error);
            setErrorMsg('Wystąpił błąd podczas wczytywania piosenki');
            setResult(false);
            setOpen(true);
        } finally {
            setIsLoading(false);
        }
    };

    const onSubmit: SubmitHandler<ISongInfoInput> = async (data) => {
        const { track, title, lyrics, author, length, songNumber } = data;
        
        let hasErrors = false;

        if (!title.trim() || !lyrics.trim() || !author.trim() || !length.trim()) {
            setErrorMsg('Wszystkie pola muszą być uzupełnione!');
            setResult(false);
            setOpen(true);
            return;
        }

        let hasChordsInVerse = false;
        let hasChordsInChorus = false;

        const versesErrors = verses?.map(verse => {
            if (verse.data.acords.trim()) hasChordsInVerse = true;
            return !verse.data.lyrics.trim();
        }) || [];

        const chorusesErrors = choruses?.map(chorus => {
            if (chorus.data.acords.trim()) hasChordsInChorus = true;
            return !chorus.data.lyrics.trim();
        }) || [];

        if (verses?.length > 0 && !hasChordsInVerse) {
            setErrorMsg('Przynajmniej jedna zwrotka musi mieć chwyty!');
            hasErrors = true;
        }
        if (choruses?.length > 0 && !hasChordsInChorus) {
            setErrorMsg('Przynajmniej jeden refren musi mieć chwyty!');
            hasErrors = true;
        }

        const melodiesErrors = melodies?.map(melody => !melody.data.lyrics.trim()) || [];

        setSectionErrors({
            verses: versesErrors,
            choruses: chorusesErrors,
            melodies: melodiesErrors,
        });

        if (hasErrors) {
            setResult(false);
            setOpen(true);
            return;
        }

        try {
            await axiosPrivate.post(
                INFO_URL,
                JSON.stringify({
                    informations: props.songNumber,
                    title,
                    lyricist: lyrics,
                    musicAuthor: author,
                    songLength: length,
                    songLengthInt: props.info.lengthInt,
                    songNumber: props.songNumber,
                }),
                {
                    headers: { 'Content-Type': 'application/json' },
                }
            );

            if (track) {
                let mp3FormData = new FormData();
                mp3FormData.append('songFile', track);
                mp3FormData.append('songFileName', track.name);
                mp3FormData.append('songNumber', props.songNumber.toString());

                await axiosPrivate.post(MP3_URL, mp3FormData);
            }

            const sendSectionData = async (
                type: 'verse' | 'chorus' | 'melody',
                data: ISongInputWrapper[]
            ) => {
                if (!data || data.length === 0) return;
                
                const url =
                    type === 'verse'
                        ? VERSE_URL
                        : type === 'chorus'
                        ? CHORUS_URL
                        : MELODY_URL;

                for (const section of data) {
                    await axiosPrivate.post(
                        url,
                        JSON.stringify({
                            songNumber: props.songNumber,
                            lyrics: section.data.lyrics,
                            acords: section.data.acords,
                            id: section.id,
                        }),
                        {
                            headers: { 'Content-Type': 'application/json' },
                        }
                    );
                }
            };

            await sendSectionData('verse', verses);
            await sendSectionData('chorus', choruses);
            await sendSectionData('melody', melodies);

            setErrorMsg(SUCCESS_MESSAGE);
            setResult(true);

            const isMounted = true;
            const controller = new AbortController();
            props.fetchData(isMounted, controller);
        } catch (error) {
            setErrorMsg(ERROR_MESSAGE);
            setResult(false);
        }

        setOpen(true);
};

    

    return (
        <div className="song-form-container">
            <SongInfo songNumber={props.songNumber} data={props.info} setInfo={props.setInfo}
                      fetchData={props.fetchData}
                      verses={verses}
                      choruses={choruses}
                      melodies={melodies}/>
            <GenerateSongStructure/>
            <ButtonGroup className="management-buttons" size="large">
                <Button
                    variant="contained"
                    size="large"
                    onClick={() =>
                        props.setInput((prev) => [
                            ...prev,
                            {
                                type: 'verse',
                                data: {lyrics: '', acords: ''},
                                id: prev.length ? prev[prev.length - 1].id + 1 : 0,
                            },
                        ])
                    }
                >
                    DODAJ ZWROTKĘ
                </Button>
                <Button
                    variant="contained"
                    size="large"
                    onClick={() =>
                        props.setInput((prev) => [
                            ...prev,
                            {
                                type: 'chorus',
                                data: {lyrics: '', acords: ''},
                                id: prev.length ? prev[prev.length - 1].id + 1 : 0,
                            },
                        ])
                    }
                >
                    DODAJ REFREN
                </Button>
                <Button
                    variant="contained"
                    size="large"
                    onClick={() =>
                        props.setInput((prev) => [
                            ...prev,
                            {
                                type: 'melody',
                                data: {lyrics: '', acords: ''},
                                id: prev.length ? prev[prev.length - 1].id + 1 : 0,
                            },
                        ])
                    }
                >
                    DODAJ CHWYTY
                </Button>
                <Button
                    variant="contained"
                    size="large"
                    className="load-song-button"
                    onClick={() => setOpenParseSongDialog(true)}
                >
                    WCZYTAJ PIOSENKĘ
                </Button>
                <Button
                    id="save-song-button"
                    variant="contained"
                    size="large"
                    onClick={() => onSubmit(props.info)}
                >
                    ZAPISZ DANE UTWORU
                </Button>
                <Button
                    id="delete-song-button"
                    variant="contained"
                    size="large"
                    onClick={props.reset}
                >
                    USUŃ UTWÓR
                </Button>
            </ButtonGroup>
            <Dialog
                open={openParseSongDialog}
                onClose={() => setOpenParseSongDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Wklej tekst piosenki</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        multiline
                        rows={15}
                        fullWidth
                        value={songText}
                        onChange={(e) => setSongText(e.target.value)}
                        placeholder="Wklej tutaj tekst piosenki..."
                        variant="outlined"
                        margin="normal"
                        disabled={isLoading}
                    />
                    {isLoading && (
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center', 
                            padding: '20px' 
                        }}>
                            <CircularProgress />
                            <span style={{ marginLeft: '10px' }}>Przetwarzanie piosenki...</span>
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleParseSong}
                        variant="contained"
                        disabled={isLoading}
                    >
                        WCZYTAJ
                    </Button>
                    <Button
                        onClick={() => setOpenParseSongDialog(false)}
                        disabled={isLoading}
                    >
                        ANULUJ
                    </Button>
                </DialogActions>
            </Dialog>
            <MessageBox open={open} setOpen={setOpen} message={errorMsg} error={!result}/>
        </div>
    );
};

export default SongForm;